/**
 * createStreamConnection: SSE stream lifecycle for the AI Assistant chat.
 *
 * Public surface:
 *   - `connect(conversationId, lastMessageId?)`: open a stream for a conversation.
 *   - `disconnect()`: close the active stream and invalidate in-flight retries.
 *   - `isStreamConnected`: reactive boolean, true between `onOpen` and the next failure/close.
 *   - `resetReconnectAttempts()`: called by ChatStore before user-initiated retries.
 *
 * ============================================================================
 * Lifecycle layers
 * ============================================================================
 *
 *   connect()                       ← public entry; resets state, calls openStream
 *      │
 *      ├──► openStream()            ← bounded: resolves on first onOpen,
 *      │       │                       rejects on error-before-open.
 *      │       │                       Mid-life errors trigger reconnectWithBackoff().
 *      │       │
 *      │       └──► createEventStream() (low-level SSE)
 *      │
 *      └── on initial failure ──► reconnectWithBackoff()
 *
 *   reconnectWithBackoff()          ← orchestrator: bounded by
 *      │                               MAX_RECONNECT_ATTEMPTS outer cycles ×
 *      │                               8 backOff retries per cycle with full
 *      │                               jitter (~60s expected recovery window).
 *      └──► openStream()            ← each backOff attempt opens a fresh stream
 *
 *   Heartbeat (scheduleHeartbeatCheck)
 *      └── if HEARTBEAT_TIMEOUT_MS elapses with no bytes (events or the
 *          server's keep-alive comments) ──► reconnectWithBackoff()
 *
 *   disconnect()                    ← public cleanup; aborts active controller,
 *                                     nulls it so all in-flight work self-cancels.
 *
 */

import { ref } from 'vue';
import { backOff } from 'exponential-backoff';

import { createEventStream } from '@/api/eventStream.ts';
import type { AssistantStreamEvent } from '@/clients/v2';

const MAX_RECONNECT_ATTEMPTS = 2;
const HEARTBEAT_INTERVAL_MS = 15000;
const HEARTBEAT_TIMEOUT_MS = HEARTBEAT_INTERVAL_MS * 2;

interface StreamConnectionOptions {
  onEvent: (event: AssistantStreamEvent) => void;
  onConnectionLost: () => void;
  onInvestigatingChange: (value: boolean) => void;
}

// Mirrors `AssistantStreamEvent['type']`.
const STREAM_EVENT_TYPES = new Set<string>([
  'connected',
  'error',
  'final',
  'status',
  'thought',
  'title',
]);

function isStreamEvent(data: unknown): data is AssistantStreamEvent {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    typeof data.type === 'string' &&
    STREAM_EVENT_TYPES.has(data.type)
  );
}

/** Thrown when a newer `connect()`/`disconnect()` replaced the controller; never retried, never shown. */
class StaleStreamError extends Error {
  constructor() {
    super('stale');
    this.name = 'StaleStreamError';
  }
}

export function createStreamConnection(options: StreamConnectionOptions) {
  const isStreamConnected = ref(false);
  const isReconnecting = ref(false);
  const abortController = ref<AbortController | null>(null);
  const reconnectAttempts = ref(0);
  const lastActivityAt = ref<number | null>(null);
  const heartbeatTimer = ref<number | null>(null);
  const lastConversationId = ref<string | null>(null);
  // The last answer streamed, so an internal reconnect resumes after it without
  // a /messages round-trip.
  const lastSeenMessageId = ref<string | null>(null);

  function clearHeartbeat() {
    if (heartbeatTimer.value) {
      clearTimeout(heartbeatTimer.value);
      heartbeatTimer.value = null;
    }
  }

  function scheduleHeartbeatCheck() {
    clearHeartbeat();
    // A stale timer must not abort a newer stream.
    const scheduledFor = abortController.value;
    heartbeatTimer.value = window.setTimeout(async () => {
      if (abortController.value !== scheduledFor) return;
      const last = lastActivityAt.value ?? 0;
      if (Date.now() - last >= HEARTBEAT_TIMEOUT_MS) {
        abortController.value?.abort();
        isStreamConnected.value = false;
        await reconnectWithBackoff();
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  function markActivity() {
    lastActivityAt.value = Date.now();
    scheduleHeartbeatCheck();
  }

  /**
   * Resolves on the stream's `onOpen`, not at its end; a later error starts `reconnectWithBackoff()`.
   * Throws `StaleStreamError` if `disconnect()` or another `openStream()` replaced the controller meanwhile.
   */
  async function openStream(conversationId: string, lastMessageId?: string): Promise<void> {
    clearHeartbeat();
    // Another conversation must not inherit the previous one's resume position.
    if (lastConversationId.value && lastConversationId.value !== conversationId) {
      lastSeenMessageId.value = null;
    }
    abortController.value?.abort();
    const myController = new AbortController();
    abortController.value = myController;
    lastConversationId.value = conversationId;

    const isCurrent = () => abortController.value === myController;

    // Retries within this cycle resume from the caller's id even before a `final` event.
    if (lastMessageId) {
      lastSeenMessageId.value = lastMessageId;
    }
    const resolvedLastMessageId = lastMessageId ?? lastSeenMessageId.value ?? undefined;

    const baseURL = `${import.meta.env.VITE_API_URL}/api/v2`;
    const url = `${baseURL}/modulos-assistant/conversations/${conversationId}/stream`;
    const headers: Record<string, string> = { Accept: 'text/event-stream' };
    if (resolvedLastMessageId) {
      headers['last-event-id'] = resolvedLastMessageId;
    }

    return new Promise<void>((resolve, reject) => {
      let opened = false;
      createEventStream({
        url,
        method: 'GET',
        headers,
        signal: myController.signal,
        onActivity: () => {
          if (isCurrent()) markActivity();
        },
        onMessage: (data: unknown) => {
          if (!isCurrent()) return;
          if (!isStreamEvent(data)) return;
          if (data.type === 'final') {
            lastSeenMessageId.value = data.id;
          }
          options.onEvent(data);
        },
        onOpen: () => {
          if (!isCurrent()) return;
          opened = true;
          isStreamConnected.value = true;
          reconnectAttempts.value = 0;
          isReconnecting.value = false;
          markActivity();
          resolve();
        },
        onError: () => {
          if (!isCurrent()) return;
          isStreamConnected.value = false;
          if (!opened) {
            reject(new Error('stream failed to open'));
          } else {
            void reconnectWithBackoff();
          }
        },
        onClose: () => {
          if (!isCurrent()) return;
          isStreamConnected.value = false;
          // A server-side close while still current is a mid-life error; opened=false means the caller's error path runs.
          if (opened) {
            void reconnectWithBackoff();
          }
        },
      });
    });
  }

  /**
   * Retries `openStream` with exponential backoff and full jitter, bounded by `MAX_RECONNECT_ATTEMPTS`
   * cycles. Single-flight: returns if a chain is active. A chain made stale by a newer
   * `connect()`/`disconnect()` exits silently.
   */
  async function reconnectWithBackoff(): Promise<void> {
    if (isReconnecting.value) return;
    if (!lastConversationId.value) return;

    if (reconnectAttempts.value >= MAX_RECONNECT_ATTEMPTS) {
      disconnect();
      options.onInvestigatingChange(false);
      options.onConnectionLost();
      return;
    }

    isReconnecting.value = true;
    reconnectAttempts.value += 1;
    // Refreshed after each own attempt, so only external connect()/disconnect() reads as stale.
    let cycleController = abortController.value;

    try {
      await backOff(
        async () => {
          if (abortController.value !== cycleController) {
            throw new StaleStreamError();
          }
          if (!lastConversationId.value) {
            throw new StaleStreamError();
          }
          try {
            await openStream(lastConversationId.value);
          } finally {
            cycleController = abortController.value;
          }
        },
        {
          delayFirstAttempt: true,
          startingDelay: 1000,
          timeMultiple: 2,
          numOfAttempts: 8,
          maxDelay: 30000,
          jitter: 'full',
          retry: (err) => !(err instanceof StaleStreamError),
        },
      );
    } catch (err) {
      if (err instanceof StaleStreamError) return;
      disconnect();
      options.onInvestigatingChange(false);
      options.onConnectionLost();
    } finally {
      // A newer chain that took over owns the flag.
      if (abortController.value === cycleController) {
        isReconnecting.value = false;
      }
    }
  }

  async function connect(conversationId: string, lastMessageId?: string): Promise<void> {
    isReconnecting.value = false;
    reconnectAttempts.value = 0;
    try {
      await openStream(conversationId, lastMessageId);
    } catch (err) {
      if (err instanceof StaleStreamError) return;
      void reconnectWithBackoff();
    }
  }

  /** Nulling the controller is what wakes sleeping backOff retries to exit. */
  function disconnect() {
    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
    isStreamConnected.value = false;
    isReconnecting.value = false;
    clearHeartbeat();
    reconnectAttempts.value = 0;
    lastActivityAt.value = null;
    lastConversationId.value = null;
    lastSeenMessageId.value = null;
  }

  function resetReconnectAttempts() {
    reconnectAttempts.value = 0;
  }

  return {
    isStreamConnected,
    connect,
    disconnect,
    resetReconnectAttempts,
  };
}
